import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Copy, Check, Pencil, RotateCcw, RefreshCw, Ban, ShieldCheck, Trash2, Monitor,
} from "lucide-react";
import { adminApi, fmtDate } from "@/api";
import { Modal, ConfirmModal } from "@/components/Modal";

const PAGE = 20;

function IconBtn({ title, onClick, disabled, danger, children, testid }) {
  return (
    <button
      type="button"
      className={`icon-btn${danger ? " icon-btn-danger" : ""}`}
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

export default function LicencesTab({ onChanged }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState(null); // {key} pour suppression
  const [copied, setCopied] = useState("");

  // Création
  const [days, setDays] = useState("");
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [uses, setUses] = useState("");

  // Édition
  const [edit, setEdit] = useState(null);

  // Set-uses
  const [setUsesFor, setSetUsesFor] = useState(null);
  const [usesAmount, setUsesAmount] = useState("");

  const load = useCallback(async (query = q, p = page) => {
    try {
      const r = await adminApi().get("/admin/licenses", {
        params: { skip: p * PAGE, limit: PAGE, q: query || undefined },
      });
      setData(r.data);
    } catch {
      toast.error("Erreur de chargement des licences");
    }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const r = await adminApi().post("/admin/licenses", {
        days_valid: days ? parseInt(days, 10) : null,
        note: note || null,
        email: email || null,
        customer_id: customerId || null,
        uses: uses ? parseInt(uses, 10) : null,
      });
      toast.success(`Licence créée : ${r.data.license_key}`);
      setShowCreate(false);
      setDays(""); setNote(""); setEmail(""); setCustomerId(""); setUses("");
      load();
      onChanged?.();
    } catch {
      toast.error("Erreur lors de la création");
    }
  };

  const saveEdit = async () => {
    try {
      await adminApi().post(`/admin/licenses/${edit.license_key}/edit`, {
        note: edit.note || null,
        email: edit.email || null,
        customer_id: edit.customer_id || null,
        uses: edit.uses === "" || edit.uses == null ? null : parseInt(edit.uses, 10),
        days_valid: edit.days_valid ? parseInt(edit.days_valid, 10) : null,
      });
      toast.success("Licence mise à jour");
      setEdit(null);
      load();
      onChanged?.();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const submitSetUses = async () => {
    const val = usesAmount.trim();
    const n = val === "" ? null : parseInt(val, 10);
    if (n !== null && (!n || n < 1)) { toast.error("Montant invalide"); return; }
    try {
      await adminApi().post(`/admin/licenses/${setUsesFor}/set-uses`, { uses: n });
      toast.success(n === null ? "Passée en illimité" : `Utilisations : ${n}`);
      setSetUsesFor(null); setUsesAmount("");
      load(); onChanged?.();
    } catch {
      toast.error("Erreur");
    }
  };

  // Actions directes (sans confirmation) — reset HWID, bannir, réactiver
  const quickAction = async (key, action) => {
    try {
      await adminApi().post(`/admin/licenses/${key}/${action}`);
      toast.success(
        action === "revoke" ? "Licence bannie"
        : action === "unban" ? "Licence réactivée"
        : "HWID réinitialisé"
      );
      load(); onChanged?.();
    } catch {
      toast.error("Erreur");
    }
  };

  const doDelete = async () => {
    try {
      await adminApi().delete(`/admin/licenses/${confirm.key}`);
      toast.success("Licence supprimée");
      setConfirm(null);
      load(); onChanged?.();
    } catch {
      toast.error("Erreur");
      setConfirm(null);
    }
  };

  const copyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(key);
      setTimeout(() => setCopied(""), 1400);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const statusTag = (l) => {
    if (!l.active) return <span className="tag t-off">Bannie</span>;
    if (l.expires_at && new Date(l.expires_at) < new Date()) return <span className="tag t-mut">Expirée</span>;
    return <span className="tag t-ok">Active</span>;
  };

  const usesDisplay = (l) => {
    if (l.uses_remaining == null) return <span className="tag t-mut">Illimité</span>;
    const total = l.uses_total != null ? l.uses_total : l.uses_remaining;
    return (
      <span className={`uses-count ${l.uses_remaining > 0 ? "" : "uses-empty"}`}>
        {l.uses_remaining} / {total}
      </span>
    );
  };

  const pages = Math.max(1, Math.ceil(data.total / PAGE));

  return (
    <div className="card lic-card" data-testid="licences-tab">
      <div className="lic-header">
        <div>
          <h2>Licences</h2>
          <p className="sub">Gérez les licences de vos clients</p>
        </div>
        <button className="btn btn-p" onClick={() => setShowCreate(true)} data-testid="new-license-btn">
          + Nouvelle licence
        </button>
      </div>

      <div className="toolrow">
        <input
          placeholder="Rechercher (clé, ID, email, note, HWID)…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          data-testid="licences-search-input"
        />
      </div>

      <div className="table-wrap">
        <table className="lic-table" data-testid="licences-table">
          <thead>
            <tr>
              <th>Clé de licence</th><th>Client</th><th>Statut</th>
              <th>HWID</th><th>Utilisations</th><th>Expire</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan="7" style={{ color: "var(--faint)" }}>Aucune licence.</td></tr>
            )}
            {data.items.map((l) => (
              <tr key={l.license_key} data-testid={`license-row-${l.license_key}`}>
                <td>
                  <div className="keycell">
                    <span className="key-pill mono">{l.license_key}</span>
                    <button
                      className="copy-btn"
                      title="Copier la clé"
                      aria-label="Copier la clé"
                      onClick={() => copyKey(l.license_key)}
                      data-testid={`copy-btn-${l.license_key}`}
                    >
                      {copied === l.license_key
                        ? <Check size={14} className="copy-ok" />
                        : <Copy size={14} />}
                    </button>
                  </div>
                </td>
                <td>
                  <span className="client-name">{l.customer_id || "—"}</span>
                  {l.note ? <div className="rowsub">{l.note}</div> : null}
                </td>
                <td>
                  <div className="badge-dual">
                    {statusTag(l)}
                    {l.hwid
                      ? <span className="tag t-info">Activée</span>
                      : <span className="tag t-mut">Non activée</span>}
                  </div>
                </td>
                <td>
                  {l.hwid ? (
                    <span className="hwid-mono" title={l.hwid}>
                      <Monitor size={13} />
                      <span className="mono">{String(l.hwid).slice(0, 14)}…</span>
                    </span>
                  ) : <span style={{ color: "var(--faint)" }}>—</span>}
                </td>
                <td data-testid={`uses-cell-${l.license_key}`}>{usesDisplay(l)}</td>
                <td className="nowrap">{l.expires_at ? fmtDate(l.expires_at) : "—"}</td>
                <td>
                  <div className="actions-row">
                    <IconBtn title="Éditer" onClick={() => setEdit({
                      ...l,
                      note: l.note || "",
                      email: l.email || "",
                      customer_id: l.customer_id || "",
                      uses: l.uses_remaining == null ? "" : String(l.uses_remaining),
                      days_valid: "",
                    })} testid={`edit-btn-${l.license_key}`}>
                      <Pencil size={15} />
                    </IconBtn>
                    <IconBtn title="Reset HWID" disabled={!l.hwid}
                      onClick={() => quickAction(l.license_key, "reset-hwid")}
                      testid={`reset-hwid-btn-${l.license_key}`}>
                      <RotateCcw size={15} />
                    </IconBtn>
                    <IconBtn title="Reset utilisations"
                      onClick={() => { setSetUsesFor(l.license_key); setUsesAmount(l.uses_total == null ? "" : String(l.uses_total)); }}
                      testid={`set-uses-btn-${l.license_key}`}>
                      <RefreshCw size={15} />
                    </IconBtn>
                    {l.active ? (
                      <IconBtn title="Bannir" danger
                        onClick={() => quickAction(l.license_key, "revoke")}
                        testid={`revoke-btn-${l.license_key}`}>
                        <Ban size={15} />
                      </IconBtn>
                    ) : (
                      <IconBtn title="Réactiver"
                        onClick={() => quickAction(l.license_key, "unban")}
                        testid={`unban-btn-${l.license_key}`}>
                        <ShieldCheck size={15} />
                      </IconBtn>
                    )}
                    <IconBtn title="Supprimer" danger
                      onClick={() => setConfirm({ key: l.license_key })}
                      testid={`delete-btn-${l.license_key}`}>
                      <Trash2 size={15} />
                    </IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="toolrow" style={{ marginTop: 14, marginBottom: 0 }}>
        <span className="hint" style={{ margin: 0 }}>{data.total} licence(s) — page {page + 1}/{pages}</span>
        <div className="spacer" />
        <button className="btn btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="licences-prev-btn">Précédent</button>
        <button className="btn btn-sm" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} data-testid="licences-next-btn">Suivant</button>
      </div>

      {showCreate && (
        <Modal title="Nouvelle licence" onClose={() => setShowCreate(false)}>
          <div className="fld">
            <label>Client / ID (ex : ID Discord) — optionnel</label>
            <input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="ex : ramz724 ou 123456789012345678" data-testid="create-license-id-input" />
          </div>
          <div className="grid2">
            <div className="fld">
              <label>Validité (jours) — vide = illimitée</label>
              <input type="number" value={days} onChange={(e) => setDays(e.target.value)} placeholder="ex : 365" data-testid="create-license-days-input" />
            </div>
            <div className="fld">
              <label>Utilisations — vide = illimité</label>
              <input type="number" min="1" value={uses} onChange={(e) => setUses(e.target.value)} placeholder="ex : 1" data-testid="create-license-uses-input" />
            </div>
          </div>
          <div className="fld">
            <label>Email client (optionnel)</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@mail.com" data-testid="create-license-email-input" />
          </div>
          <div className="fld">
            <label>Note (optionnel)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex : commande #123" data-testid="create-license-note-input" />
          </div>
          <div className="actions">
            <button className="btn" onClick={() => setShowCreate(false)}>Annuler</button>
            <button className="btn btn-p" onClick={create} data-testid="create-license-submit-btn">Créer</button>
          </div>
        </Modal>
      )}

      {edit && (
        <Modal title="Éditer la licence" onClose={() => setEdit(null)}>
          <p className="mono" style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14 }}>{edit.license_key}</p>
          <div className="fld">
            <label>Client / ID (ex : ID Discord)</label>
            <input value={edit.customer_id} onChange={(e) => setEdit({ ...edit, customer_id: e.target.value })} placeholder="ex : ramz724" data-testid="edit-license-id-input" />
          </div>
          <div className="grid2">
            <div className="fld">
              <label>Utilisations — vide = illimité</label>
              <input type="number" min="1" value={edit.uses} onChange={(e) => setEdit({ ...edit, uses: e.target.value })} data-testid="edit-license-uses-input" />
            </div>
            <div className="fld">
              <label>Validité (jours) — vide = illimitée</label>
              <input type="number" min="1" value={edit.days_valid} onChange={(e) => setEdit({ ...edit, days_valid: e.target.value })} placeholder="recalcule l'expiration" data-testid="edit-license-days-input" />
            </div>
          </div>
          <div className="fld">
            <label>Email client</label>
            <input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="client@mail.com" data-testid="edit-license-email-input" />
          </div>
          <div className="fld">
            <label>Note</label>
            <input value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} data-testid="edit-license-note-input" />
          </div>
          <div className="actions">
            <button className="btn" onClick={() => setEdit(null)}>Annuler</button>
            <button className="btn btn-p" onClick={saveEdit} data-testid="edit-license-submit-btn">Enregistrer</button>
          </div>
        </Modal>
      )}

      {setUsesFor && (
        <Modal title="Réinitialiser les utilisations" onClose={() => setSetUsesFor(null)}>
          <p className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>{setUsesFor}</p>
          <div className="fld">
            <label>Nouveau nombre d&apos;utilisations — vide = illimité</label>
            <input type="number" min="1" value={usesAmount} onChange={(e) => setUsesAmount(e.target.value)} placeholder="ex : 3" data-testid="set-uses-amount-input" />
          </div>
          <div className="actions">
            <button className="btn" onClick={() => setSetUsesFor(null)}>Annuler</button>
            <button className="btn btn-p" onClick={submitSetUses} data-testid="set-uses-submit-btn">Appliquer</button>
          </div>
        </Modal>
      )}

      {confirm && (
        <ConfirmModal
          title="Supprimer la licence"
          body={`La licence ${confirm.key} sera SUPPRIMÉE définitivement. Cette action est irréversible.`}
          danger
          onConfirm={doDelete}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
